import { FileDown, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { descargarReporteLoto } from "@/lib/export";

/**
 * ReportePdfButtonProps identifica el LOTO cuyo reporte se descarga: la obra activa, el id del
 * LOTO y su número (para nombrar el archivo LOTO-2026-0001.pdf).
 */
type ReportePdfButtonProps = {
  obraId: number;
  lotoId: number;
  numeroLoto: string;
};

/**
 * ReportePdfButton descarga el reporte PDF del LOTO (#213): datos, cronología, candados y
 * registro fotográfico (GET /api/v1/obras/{obraId}/lotos/{id}/reporte.pdf, lectura onboarded).
 * Mantiene su propio estado de carga/fallo y muestra el error inline —mismo patrón que
 * ExportButton— para no depender de un sistema de toasts.
 */
export function ReportePdfButton({ obraId, lotoId, numeroLoto }: ReportePdfButtonProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  async function onDescargar() {
    setPending(true);
    setError(false);
    try {
      await descargarReporteLoto(obraId, lotoId, numeroLoto);
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-stretch gap-1">
      <Button
        variant="outline"
        onClick={() => void onDescargar()}
        disabled={pending}
        aria-label="Descargar reporte PDF"
      >
        {pending ? (
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
        ) : (
          <FileDown className="h-5 w-5" aria-hidden="true" />
        )}
        <span className="hidden sm:inline">Descargar PDF</span>
      </Button>
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          No se pudo generar el PDF.
        </span>
      ) : null}
    </div>
  );
}
